import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Calendar, CheckCircle, CreditCard } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import GradCapLoader from "@/components/ui/grad-cap-loader";

export const Route = createFileRoute("/classes/sign-up-confirmed")({
  component: RouteComponent,
  validateSearch: z.object({
    email: z.string().optional(),
    transactionId: z.number().optional(),
  }),
});

function RouteComponent() {
  const search = Route.useSearch();
  return (
    <div>
      <div className="bg-white shadow-md rounded-lg">
        <CardContent className="pt-6">
          <div className="text-center py-8 px-4">
            <CheckCircle className="mx-auto h-16 w-16 text-primary mb-4" />
            <h2 className="text-2xl font-bold mb-2">Registration Complete!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for registering. A confirmation email has been sent to {search.email}.
            </p>

            {/* Next Steps Section */}
            <div className="bg-secondary/50 border border-secondary rounded-lg p-6 mb-6 text-left max-w-2xl mx-auto">
              <h3 className="text-xl font-semibold mb-4 text-center text-secondary-foreground">
                📋 Next Steps: Complete Your Payment
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <p className="text-foreground">
                    <strong>View Your Classes:</strong> Your registered classes and upcoming events
                    are now visible on your dashboard. The url link to join each class is located on your dashboard. Click on an event to see more details.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CreditCard className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <p className="text-foreground">
                    <strong>Complete Payment:</strong> Click the <strong>"Pay Now"</strong> button
                    next to your transaction to finalize your enrollment.
                  </p>
                </div>
                <div className="bg-accent border border-accent-foreground/20 rounded p-3 mt-4">
                  <p className="text-sm text-accent-foreground">
                    ⚠️ <strong>Important:</strong> Your spot is reserved, but payment must be
                    completed to secure your enrollment in the class.
                  </p>
                </div>
              </div>
            </div>

            {/* Primary CTA */}
            <div className="flex flex-col items-center gap-3 w-full max-w-md mx-auto">
              {search.transactionId && (
                <Link
                  to="/clientdashboard/stripe/checkout"
                  search={{ transactionIds: [search.transactionId] }}
                  className="w-full"
                >
                  <Button size="lg" className="w-full text-lg bg-green-600 hover:bg-green-700">
                    <CreditCard className="mr-2 h-5 w-5" />
                    Pay Now
                  </Button>
                </Link>
              )}
              <Link to="/clientdashboard" className="w-full">
                <Button
                  size="lg"
                  variant={search.transactionId ? "outline" : "default"}
                  className="w-full text-lg"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link
                to="/"
                className="mt-2 text-muted-foreground hover:text-foreground hover:underline text-sm"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </CardContent>
      </div>
      <GradCapLoader />
    </div>
  );
}
